import { useCallback, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { formatUnits, parseUnits } from 'viem'

import {
  SwapParams,
  SwapRequest,
} from '~/components/forms/swap/SwapFormSection'
import { toSwapQuoteError } from '~/components/forms/swap/hooks/swapQuoteError'
import {
  clearQuoteError,
  getQuoteRefetchInterval,
  recordQuoteError,
  shouldRetryQuote,
} from '~/components/forms/swap/hooks/swapQuotePolling'
import { SwapDirection, SwapRouter } from '~/components/forms/swap/utils'
import { SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_SWAP_QUOTE } from '~/constants/refetch-intervals'
import { useActiveOrgId } from '~/hooks/org'
import { ServerGetArgs, useServerRequests } from '~/hooks/useRequests'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { BackendLoggingData, BackendTransactionCall, IAddress } from '~/types'
import { customStringify } from '~/utils'
import { Address, EvmAddress } from '~/utils/address'
import {
  bpsToPercentage,
  DEFAULT_SLIPPAGE_BPS,
  percentageToBps,
} from '~/utils/swap'

export const REFRESH_QUOTE_INTERVAL = REFETCH_SWAP_QUOTE

type SwapQuoteRequest = {
  chainId: SupportedChainId
  destinationChainId?: SupportedChainId
  sender: IAddress
  buyToken: IAddress
  sellToken: IAddress
  sellAmount?: string // Token amount as returned by parseUnits, e.g. '123456789'.
  buyAmount?: string // Token amount as returned by parseUnits, e.g. '123456789'.
  receiver?: IAddress
  loggingData?: BackendLoggingData
}

type BaseSwapQuoteResponse = Omit<
  BaseSwapQuote,
  'sellAmount' | 'buyAmount' | 'steps'
> & {
  sellAmount: string // Token amount as returned by parseUnits, e.g. '123456789'.
  buyAmount: string // Token amount as returned by parseUnits, e.g. '123456789'.
  steps?: SwapQuoteResponse[]
}

type BridgeSwapQuoteResponse = BaseSwapQuoteResponse & {
  providerTransactionId: string
}

type SwapQuoteResponse = BaseSwapQuoteResponse | BridgeSwapQuoteResponse

type BaseSwapQuote = {
  chainId: SupportedChainId
  destinationChainId: SupportedChainId
  sellToken: IAddress
  sellAmount: bigint
  buyToken: IAddress
  buyAmount: bigint
  sender: IAddress
  receiver: IAddress
  routerType: SwapRouter
  calls: BackendTransactionCall[]
  estimatedGas?: string
  estimatedGasCost?: string
  swapFeeBps?: number
  /** Base plan rate for this pair as if plans were enforced, in basis points */
  standardSwapFeeBps?: number
  fee?: {
    amount: string
    token: IAddress
  }
  steps?: SwapQuote[]
}

export type BridgeSwapQuote = BaseSwapQuote & {
  providerTransactionId: string
}

export type SwapQuote = BaseSwapQuote | BridgeSwapQuote

export const isBridgeSwapQuote = (
  swapQuote: SwapQuote,
): swapQuote is BridgeSwapQuote => 'providerTransactionId' in swapQuote

type GetLoggingDataParams = {
  routerType?: string
  fee?: SwapQuote['fee']
  buyAmountOverride?: string
  sellAmountOverride?: string
}

export const useGetSwapLoggingData = ({
  sellToken,
  sellAmount,
  sellChainId,
  buyToken,
  buyAmount,
  buyChainId,
  sender,
  isPrivate,
  recipients,
  orgId,
  slippage,
}: {
  sellToken?: IAddress
  sellAmount: string
  sellChainId?: number
  buyToken?: IAddress
  buyAmount: string
  buyChainId?: number
  isPrivate?: boolean
  sender: IAddress
  recipients: {
    address: IAddress | undefined
  }[]
  slippage?: string
  orgId?: string
}): {
  getLoggingData: (args: GetLoggingDataParams) => BackendLoggingData | undefined
} => {
  const sellTokenMetadata = useTokenMetadata({
    address: sellToken,
    chainId: sellChainId,
  })
  const buyTokenMetadata = useTokenMetadata({
    address: buyToken,
    chainId: buyChainId,
  })

  const { convertTokenToUSD } = useTokenPrices()

  const getLoggingData = useCallback(
    ({
      routerType,
      fee,
      buyAmountOverride,
      sellAmountOverride,
    }: GetLoggingDataParams) => {
      const buyAmountNumber = parseUnits(
        buyAmountOverride ?? buyAmount ?? '0',
        buyTokenMetadata?.decimals ?? 18,
      )
      const sellAmountNumber = parseUnits(
        sellAmountOverride ?? sellAmount ?? '0',
        sellTokenMetadata?.decimals ?? 18,
      )

      const buyUSDValue =
        buyToken && buyChainId
          ? convertTokenToUSD({
              token: buyToken,
              amount: buyAmountNumber,
              chainId: buyChainId,
            })
          : undefined
      const sellUSDValue =
        sellToken && sellChainId
          ? convertTokenToUSD({
              token: sellToken,
              amount: sellAmountNumber,
              chainId: sellChainId,
            })
          : undefined

      const sellTokenSymbol = sellTokenMetadata?.symbol ?? 'unknown'
      const buyTokenSymbol = buyTokenMetadata?.symbol ?? 'unknown'

      const loggingData = {
        sellToken,
        sellTokenSymbol,
        sellAmount,
        sellAmountUsd: sellUSDValue ?? 0,
        sellChainId,
        buyToken,
        buyTokenSymbol,
        buyAmount,
        buyAmountUsd: buyUSDValue ?? 0,
        buyChainId,
        private: isPrivate,
        tokenPair: [sellToken, buyToken].join('-'),
        orderedTokenPair: [sellToken, buyToken].sort().join('-'),
        tokenPairSymbols: [sellTokenSymbol, buyTokenSymbol].join('-'),
        orderedTokenPairSymbols: [sellTokenSymbol, buyTokenSymbol]
          .sort()
          .join('-'),
        sender,
        recipients: recipients
          .map((recipient) => recipient.address)
          .filter((address): address is IAddress => !!address),
        orgId,
        slippage: slippage ?? 'unknown',
      }

      return {
        ...loggingData,
        ...(routerType
          ? {
              routerType,
            }
          : {}),
        ...(fee
          ? {
              feeAmount: fee.amount,
              feeToken: fee.token,
            }
          : {}),
      }
    },
    [
      sellToken,
      sellTokenMetadata?.symbol,
      sellAmount,
      sellChainId,
      buyToken,
      buyTokenMetadata?.symbol,
      buyAmount,
      buyChainId,
      sender,
      recipients,
      orgId,
      isPrivate,
      convertTokenToUSD,
      sellTokenMetadata?.decimals,
      buyTokenMetadata?.decimals,
      slippage,
    ],
  )

  return { getLoggingData }
}

export const getSwapQuoteKey = (params?: SwapRequest, reverse?: boolean) => {
  const queryKeyParams = params
    ? {
        ...params,
        slippage: params.slippage
          ? percentageToBps(Number(params.slippage))
          : undefined,
        loggingData: undefined, // Remove loggingData from cache key
        reverse: !!reverse, // Differentiate reversed quotes from originals
      }
    : undefined

  return customStringify(queryKeyParams)
}

export const MIN_SLIPPAGE_BPS = 1 // 0.01%
export const MAX_SLIPPAGE_BPS = 10000 // 100%

/**
 * @param params - The swap request parameters.
 * @param options.silent - Suppress the quote-failure toast (the query still
 *   fails so callers can surface inline validation instead).
 * @returns The swap quote.
 */
export const useSwapQuoteQuery = (
  params?: SwapRequest,
  options?: { silent?: boolean },
) => {
  const { serverGetRequest } = useServerRequests()
  return useQuery({
    queryKey: [QUERY_KEYS.SWAP_QUOTE, getSwapQuoteKey(params)],
    queryFn: async () => {
      if (!params) throw new Error('Invalid params')

      const quote = await getSwapQuote(params, serverGetRequest, {
        silent: options?.silent,
      })

      return {
        success: true,
        queryKey: getSwapQuoteKey(params),
        data: quote,
      }
    },
    enabled: !!params,
    staleTime: REFRESH_QUOTE_INTERVAL,
    refetchInterval: (query) =>
      getQuoteRefetchInterval({ params, error: query.state.error }),
    retry: shouldRetryQuote,
    retryDelay: 5_000,
    refetchOnWindowFocus: false,
  })
}

/** The cached quote, only when it was fetched for the live request rather than a stale one. */
export const getCurrentSwapQuote = (
  swapQuote: { queryKey: string; data: SwapQuote } | undefined,
  swapRequest: SwapRequest | undefined,
) =>
  swapQuote && swapQuote.queryKey === getSwapQuoteKey(swapRequest)
    ? swapQuote.data
    : undefined

/**
 * Calculates a swap in the opposite direction, aiming to get as close as possible to the given quote's buy and sell amounts.
 *
 * This is useful to check if one direction is far more expensive than another (common with Relay for exact-output swaps).
 *
 * `quote` is a required parameter (for the query to run) because the original direction's input/output amounts are used as a starting point for the reverse swap's exhange rate.
 */
export const useReverseSwapQuoteQuery = (
  params: SwapRequest | undefined,
  originalQuote: SwapQuote | undefined,
) => {
  const { serverGetRequest } = useServerRequests()
  const destinationTokenMetadata = useTokenMetadata({
    address: params?.destinationToken.token,
    chainId: params?.destinationToken.chainId,
  })
  const sourceTokenMetadata = useTokenMetadata({
    address: params?.sourceToken.token,
    chainId: params?.sourceToken.chainId,
  })

  /**
   * This query uses a two-pass approach. We first get the general exchange rate using the existing BUY amounts. This exchange rate will be less favorable given the user has chosen to switch from the BUY exchange rate to a SELL one.
   *
   * We then get the SELL exchange rate using the BUY request's amounts and exchange rate. This gives us a SELL exchange rate that applies roughly to the amounts being swapped but matches the SELL direction. Using this exchange rate we determine the final INPUT amount needed to reach the desired OUTPUT amount that was specified in the original BUY quote.
   */
  return useQuery({
    queryKey: [QUERY_KEYS.SWAP_QUOTE, getSwapQuoteKey(params, true)],
    queryFn: async () => {
      if (!params || !originalQuote) throw new Error('Invalid params')

      // At some point in the future we may need to reverse sell-direction swaps, but for now we only support buy-direction swaps
      if (params.swapDirection !== SwapDirection.BUY)
        throw new Error('Only buy direction swaps can be reversed.')

      const requestedDecimalAmount = Number(
        formatUnits(
          params.destinationToken.amount,
          destinationTokenMetadata.decimals,
        ),
      )
      // BUY tokens per SELL token
      const startingExchangeRate =
        Number(
          formatUnits(
            originalQuote.buyAmount,
            destinationTokenMetadata.decimals,
          ),
        ) /
        Number(
          formatUnits(originalQuote.sellAmount, sourceTokenMetadata.decimals),
        )

      const quoteParams: SwapParams = {
        ...params,
        swapDirection: SwapDirection.SELL,
        sourceToken: {
          ...params.sourceToken,
          amount: parseUnits(
            (requestedDecimalAmount / startingExchangeRate).toString(),
            sourceTokenMetadata.decimals,
          ),
        },
      }

      const initialQuote = await getSwapQuote(quoteParams, serverGetRequest, {
        silent: true,
      })
      const adjustedExchangeRate =
        Number(
          formatUnits(
            initialQuote.buyAmount,
            destinationTokenMetadata.decimals,
          ),
        ) /
        Number(
          formatUnits(initialQuote.sellAmount, sourceTokenMetadata.decimals),
        )
      // Increase the desired output amount by the slippage tolerance (which is in percent) to guarantee a minimum amount
      quoteParams.sourceToken.amount = parseUnits(
        (
          (requestedDecimalAmount *
            (1 +
              Number(
                params.slippage ??
                  bpsToPercentage(DEFAULT_SLIPPAGE_BPS).toString(),
              ) /
                100)) /
          adjustedExchangeRate
        ).toString(),
        sourceTokenMetadata.decimals,
      )

      const quote = await getSwapQuote(quoteParams, serverGetRequest, {
        silent: true,
      })

      return {
        success: true,
        queryKey: getSwapQuoteKey(params, true), // This query key is used to determine quote readiness from the **original** swap request (not reversed)
        data: quote,
      }
    },
    throwOnError: false,
    enabled: !!params && !!originalQuote,
    staleTime: REFRESH_QUOTE_INTERVAL,
    refetchInterval: (query) =>
      getQuoteRefetchInterval({ params, error: query.state.error }),
    retry: shouldRetryQuote,
    retryDelay: 5_000,
    refetchOnWindowFocus: false,
  })
}

const getSwapQuote = async (
  params: SwapParams,
  serverGetRequest: <ServerResponseType>(
    args: ServerGetArgs,
  ) => Promise<AxiosResponse<ServerResponseType> | void>,
  options?: { silent?: boolean },
): Promise<SwapQuote> => {
  const slippageBps = params.slippage
    ? percentageToBps(Number(params.slippage))
    : DEFAULT_SLIPPAGE_BPS

  if (slippageBps < MIN_SLIPPAGE_BPS || slippageBps > MAX_SLIPPAGE_BPS) {
    throw new Error('Invalid slippage')
  }

  const baseReq = {
    sender: params.account,
    chainId: params.sourceToken.chainId,
    sellToken: params.sourceToken.token as IAddress,
    destinationChainId: params.destinationToken.chainId,
    buyToken: params.destinationToken.token as IAddress,
    receiver: params.receiver ?? params.account,
    loggingData: params.loggingData,
    verifyBalance: params.verifyBalance ?? false,
    private: params.private ?? false,
    slippage: slippageBps,
  }

  // Only add the relevant amount based on direction
  const amountParams =
    params.swapDirection === SwapDirection.SELL
      ? { sellAmount: params.sourceToken.amount.toString() }
      : { buyAmount: params.destinationToken.amount.toString() }

  const req: SwapQuoteRequest = {
    ...baseReq,
    ...amountParams,
  }

  const { loggingData, ...rest } = req

  // serverGetRequest swallows failures (returning void), so capture the Axios
  // error here to preserve the server's structured quote-error payload.
  let requestError: unknown
  const response = await serverGetRequest<{ quote: SwapQuoteResponse }>({
    path: `/api/public/swap/quote`,
    params: {
      ...rest,
      ...(loggingData
        ? {
            loggingData: JSON.stringify(loggingData),
          }
        : {}),
    },
    onError: (e) => {
      requestError = e
    },
    ...(options?.silent
      ? {}
      : {
          errorMessage: 'Failed to fetch quote',
          errorToastId: 'swap-quote-error',
        }),
  })

  if (!response) {
    const error = toSwapQuoteError(requestError)
    recordQuoteError({ params, error })
    throw error
  }
  clearQuoteError(params)

  return parseSwapQuote(response.data.quote)
}

// Steps carry the same string amounts as the top-level quote.
const parseSwapQuote = (quote: SwapQuoteResponse): SwapQuote => ({
  ...quote,
  sellAmount: BigInt(quote.sellAmount),
  buyAmount: BigInt(quote.buyAmount),
  steps: quote.steps?.map(parseSwapQuote),
})

interface UseSwapRequestParams {
  account: IAddress | undefined
  sourceChainId: number | undefined
  sourceToken: IAddress | undefined
  sourceAmount: string | undefined
  destinationToken: IAddress | undefined
  destinationChainId: number | undefined
  destinationAmount: string | undefined
  isPrivate?: boolean
  swapDirection: SwapDirection
  receiver?: IAddress
  verifyBalance?: boolean
  slippage?: string
}

export const useSwapRequest = ({
  account,
  sourceChainId,
  sourceToken,
  sourceAmount,
  destinationToken,
  destinationChainId,
  destinationAmount,
  isPrivate,
  swapDirection,
  receiver,
  verifyBalance,
  slippage,
}: UseSwapRequestParams): SwapRequest => {
  const sourceTokenMetadata = useTokenMetadata({
    address: sourceToken,
    chainId: sourceChainId,
  })
  const destinationTokenMetadata = useTokenMetadata({
    address: destinationToken,
    chainId: destinationChainId,
  })

  const activeOrgId = useActiveOrgId()

  const { getLoggingData } = useGetSwapLoggingData({
    sellAmount: sourceAmount ?? '0',
    sellToken: sourceToken,
    sellChainId: sourceChainId,
    buyToken: destinationToken,
    buyChainId: destinationChainId,
    buyAmount: destinationAmount ?? '0',
    sender: account ?? ('0x' as IAddress),
    recipients: useMemo(
      () => (receiver ? [{ address: receiver }] : []),
      [receiver],
    ),
    orgId: activeOrgId,
    slippage,
  })

  return useMemo(() => {
    // A swap from a token into itself on the same chain is just a direct
    // transfer — skip quote fetching so the caller falls back to transfer calls.
    // We still want to get a quote for private transfers since we must go through a provider
    if (
      sourceChainId === destinationChainId &&
      Address.equal(sourceToken, destinationToken) &&
      !isPrivate
    ) {
      return undefined
    }

    if (
      account &&
      sourceChainId &&
      sourceToken &&
      destinationToken &&
      destinationChainId
    ) {
      if (sourceAmount && swapDirection === SwapDirection.SELL) {
        const sourceAmountBigInt = parseUnits(
          sourceAmount,
          sourceTokenMetadata?.decimals ?? 18,
        )
        if (sourceAmountBigInt === BigInt(0)) {
          return undefined
        }
        return {
          account,
          sourceToken: {
            amount: sourceAmountBigInt,
            chainId: sourceChainId,
            token: EvmAddress.format(sourceToken),
          },
          destinationToken: {
            token: EvmAddress.format(destinationToken),
            chainId: destinationChainId,
          },
          receiver,
          private: isPrivate,
          swapDirection: SwapDirection.SELL,
          loggingData: getLoggingData({}),
          slippage,
        }
      } else if (destinationAmount && swapDirection === SwapDirection.BUY) {
        const destinationAmountBigInt = parseUnits(
          destinationAmount,
          destinationTokenMetadata?.decimals ?? 18,
        )
        if (destinationAmountBigInt === BigInt(0)) {
          return undefined
        }
        return {
          account,
          sourceToken: {
            token: sourceToken,
            chainId: sourceChainId,
          },
          destinationToken: {
            token: destinationToken,
            chainId: destinationChainId,
            amount: destinationAmountBigInt,
          },
          private: isPrivate,
          receiver,
          verifyBalance,
          swapDirection: SwapDirection.BUY,
          loggingData: getLoggingData({}),
          slippage,
        }
      }
    }
    return undefined
  }, [
    account,
    sourceChainId,
    sourceToken,
    destinationToken,
    destinationChainId,
    sourceAmount,
    swapDirection,
    destinationAmount,
    isPrivate,
    sourceTokenMetadata?.decimals,
    receiver,
    getLoggingData,
    destinationTokenMetadata?.decimals,
    verifyBalance,
    slippage,
  ])
}
